import React, { useEffect } from "react";
import { Box } from "@mui/material";
import { Process, Service } from "../../../api/processes";
import { ProcessSection } from "../../../api/process-sections";
import {
  useProcessForm,
  useStageManagement,
  useReasonSolutionManagement,
  useSectionDeselectConfirmation,
} from "../hooks";
import {
  ProcessBasicInfo,
  ProcessSectionSelector,
  StageList,
  StageEditForm,
  ReasonSolutionForm,
  ProcessFormSubmit,
  ConfirmationDialog,
} from "./";
import {
  mainContainerStyles,
  processSectionsContainerStyles,
  sectionTitleStyles,
} from "../styles";

interface ProcessFormProps {
  process?: Process | null;
  services: Service[];
  processSections?: ProcessSection[];
  followUpGroups?: any[];
  onFormDataChange?: (formData: any) => void;
}

export function ProcessForm({
  process,
  services,
  processSections = [],
  followUpGroups = [],
  onFormDataChange,
}: ProcessFormProps) {
  // Main form hook
  const {
    formData,
    errors,
    selectedSections,
    stages,
    reasonSolutions,
    sectionVisibility,
    editingStage,
    editingReasonSolution,
    stageError,
    reasonSolutionError,
    setEditingStage,
    setEditingReasonSolution,
    setStageError,
    setReasonSolutionError,
    setStages,
    setReasonSolutions,
    validateForm,
    handleChange,
    handleSectionToggle,
    handleSectionDeselect,
    handleToggleSectionVisibility,
    getStagesForSection,
    getReasonSolutionsForStage,
  } = useProcessForm(process, services, processSections);

  // Confirmation dialog hook
  const {
    confirmationDialog,
    showConfirmation,
    hideConfirmation,
    confirmDeselect,
  } = useSectionDeselectConfirmation();

  // Stage management hook
  const {
    handleAddStageClick,
    handleSaveStage,
    handleEditStage,
    handleDeleteStage,
    handleCancelEditingStage,
  } = useStageManagement(
    stages,
    setStages,
    editingStage,
    setEditingStage,
    setStageError,
    getStagesForSection
  );

  // Reason solution management hook
  const {
    handleAddReasonSolutionClick,
    handleSaveReasonSolution,
    handleDeleteReasonSolution,
    handleEditReasonSolution,
    handleCancelEditingReasonSolution,
  } = useReasonSolutionManagement(
    stages,
    reasonSolutions,
    setReasonSolutions,
    editingReasonSolution,
    setEditingReasonSolution,
    setReasonSolutionError,
    getReasonSolutionsForStage
  );

  // Confirmation dialog handlers
  const handleConfirmDeselect = () => {
    confirmDeselect(handleSectionDeselect);
  };

  // Pass form data to parent component
  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange({
        formData,
        selectedSections,
        stages,
        reasonSolutions,
        validateForm,
      });
    }
  }, [
    formData,
    selectedSections,
    stages,
    reasonSolutions,
    validateForm,
    onFormDataChange,
  ]);

  return (
    <Box component="form" sx={mainContainerStyles}>
      {/* Basic Information */}
      <ProcessBasicInfo
        formData={formData}
        errors={errors}
        services={services}
        loading={false}
        onChange={handleChange}
      />

      {/* Process Sections */}
      <Box sx={processSectionsContainerStyles}>
        <Box sx={sectionTitleStyles}>انتخاب بخش‌های فرایند</Box>
        <ProcessSectionSelector
          processSections={processSections}
          selectedSections={selectedSections}
          onSectionToggle={handleSectionToggle}
          onSectionDeselect={handleSectionDeselect}
          onAddStageClick={handleAddStageClick}
          isEditing={!!process}
          onShowConfirmation={showConfirmation}
          sectionVisibility={sectionVisibility}
          onToggleSectionVisibility={handleToggleSectionVisibility}
          // Stage Edit Form props
          editingStage={editingStage}
          stageError={stageError}
          onSaveStage={handleSaveStage}
          onCancelEditingStage={handleCancelEditingStage}
          onStageNumberChange={(value) =>
            setEditingStage((prev) => ({
              ...prev,
              stageNumber: value,
            }))
          }
          onTitleChange={(value) =>
            setEditingStage((prev) => ({ ...prev, title: value }))
          }
          // Stage List props
          stages={stages}
          reasonSolutions={reasonSolutions}
          onAddReasonSolutionClick={handleAddReasonSolutionClick}
          onEditStage={handleEditStage}
          onDeleteStage={handleDeleteStage}
          onEditReasonSolution={handleEditReasonSolution}
          onDeleteReasonSolution={handleDeleteReasonSolution}
          getReasonSolutionsForStage={getReasonSolutionsForStage}
          // Reason Solution Form props
          editingReasonSolution={editingReasonSolution}
          reasonSolutionError={reasonSolutionError}
          onSaveReasonSolution={handleSaveReasonSolution}
          onCancelEditingReasonSolution={handleCancelEditingReasonSolution}
          onRowChange={(value) =>
            setEditingReasonSolution((prev) => ({
              ...prev,
              row: value,
            }))
          }
          onIsPrimaryChange={(value) =>
            setEditingReasonSolution((prev) => ({
              ...prev,
              isPrimary: value,
            }))
          }
          onReasonChange={(value) =>
            setEditingReasonSolution((prev) => ({
              ...prev,
              reason: value,
            }))
          }
          onSolutionChange={(value) =>
            setEditingReasonSolution((prev) => ({
              ...prev,
              solution: value,
            }))
          }
          onPreviousReasonIdsChange={(value) =>
            setEditingReasonSolution((prev) => ({
              ...prev,
              previousReasonIdsJson: value,
            }))
          }
          // Stage Edit Form props
          onIsPublicChange={(value) =>
            setEditingStage((prev) => ({
              ...prev,
              isPublic: value,
            }))
          }
          onFollowUpGroupIdsChange={(value) =>
            setEditingStage((prev) => ({
              ...prev,
              followUpGroupIdsJson: value,
            }))
          }
          followUpGroups={followUpGroups}
        >
          {/* StageList will be rendered inside ProcessSectionSelector for each section */}
        </ProcessSectionSelector>
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={hideConfirmation}
        onConfirm={handleConfirmDeselect}
        title="تایید حذف بخش"
        message={`آیا مطمئن هستید که می‌خواهید بخش "${confirmationDialog.sectionName}" را از حالت انتخاب خارج کنید؟ با این کار تمامی مراحل و راه‌حل‌های مربوط به این بخش حذف خواهند شد و این عمل قابل بازگشت نیست.`}
        confirmText="بله، حذف کن"
        cancelText="انصراف"
        severity="warning"
      />
    </Box>
  );
}
